import React from 'react';

type Column = {
  title?: string;
  description?: string;
  bulletPoints?: string[];
};

type TwoColProps =
  | {
      problem?: Column;
      background?: Column;
    }
  | {
      problemTitle?: string;
      problemDescription?: string;
      solutionTitle?: string;
      solutionDescription?: string;
      bulletPoints?: string[];
    };

export const TwoColumnProblemOverview: React.FC<TwoColProps> = (props) => {
  // Normalize props to support both legacy and new API shapes
  const problem: Column = props.problem ?? {
    title: (props as any).problemTitle,
    description: (props as any).problemDescription,
  };

  const background: Column = props.background ?? {
    title: (props as any).solutionTitle,
    description: (props as any).solutionDescription,
    bulletPoints: (props as any).bulletPoints,
  };

  // If neither side has meaningful content, render nothing
  const hasProblem = problem && (problem.title || problem.description);
  const hasBackground =
    background && (background.title || background.description || (background.bulletPoints?.length ?? 0) > 0);
  if (!hasProblem && !hasBackground) return null;

  const problemParagraphs = (problem?.description ?? '').split('\n').filter(Boolean);
  const backgroundParagraphs = (background?.description ?? '').split('\n').filter(Boolean);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 mt-10">
      {/* Problem Column */}
      <div className="bg-white dark:bg-zinc-950 p-8 pl-0 rounded-xl shadow-sm">
        {problem?.title && (
          <h3 className="text-xl mb-6 dark:text-white">{problem.title}</h3>
        )}
        {problemParagraphs.length > 0 && (
          <div className="text-zinc-700 dark:text-zinc-300 text-xl leading-relaxed space-y-4">
            {problemParagraphs.map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
        )}
      </div>

      {/* Background/Overview Column */}
      <div className="bg-white dark:bg-zinc-950 p-8 rounded-xl shadow-sm">
        {background?.title && (
          <h3 className="text-xl mb-6 dark:text-white">{background.title}</h3>
        )}
        {backgroundParagraphs.length > 0 && (
          <div className="text-zinc-700 dark:text-zinc-300 text-lg leading-relaxed mb-6">
            {backgroundParagraphs.map((paragraph, i) => (
              <p key={i} className="mb-4">
                {paragraph}
              </p>
            ))}
          </div>
        )}

        {background?.bulletPoints && background.bulletPoints.length > 0 && (
          <ul className="space-y-4">
            {background.bulletPoints.map((point, index) => (
              <li key={index} className="flex items-start">
                <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-2.5 mr-3 flex-shrink-0"></span>
                <span className="text-zinc-700 dark:text-zinc-300">{point}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
